import { Button, Space, message } from 'antd';
import { SaveOutlined, CloseOutlined, CopyOutlined } from '@ant-design/icons';
import { Trans } from '@lingui/macro';

export type JsonActionButtonsProps = {
  hasChanges?: boolean;
  error?: string | null;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  size?: 'small' | 'middle' | 'large';
  className?: string;
};

/**
 * Reusable Save/Cancel button group for JSON editing
 */
export const JsonSaveCancelButtons = ({
  hasChanges,
  error,
  onSave,
  onCancel,
  isSaving = false,
  size = 'small',
  className,
}: JsonActionButtonsProps) => {
  return (
    <div className={className}>
      <Space size="small">
        <Button
          type="primary"
          size={size}
          icon={<SaveOutlined />}
          onClick={onSave}
          disabled={!!error || !hasChanges || isSaving}
          loading={isSaving}
        >
          <Trans>Save</Trans>
        </Button>
        <Button
          size={size}
          icon={<CloseOutlined />}
          onClick={onCancel}
          disabled={isSaving}
        >
          <Trans>Cancel</Trans>
        </Button>
      </Space>
    </div>
  );
};

export type JsonCopyButtonProps = {
  value: Record<string, unknown> | null;
  size?: 'small' | 'middle' | 'large';
  type?: 'text' | 'default' | 'primary' | 'link' | 'dashed';
  icon?: React.ReactNode;
  successMessage?: string;
  errorMessage?: string;
};

/**
 * Reusable Copy JSON button with clipboard functionality
 */
export const JsonCopyButton = ({
  value,
  size = 'small',
  type = 'text',
  icon = <CopyOutlined />,
  successMessage,
  errorMessage,
}: JsonCopyButtonProps) => {
  const handleCopy = async () => {
    try {
      const jsonString = JSON.stringify(value, null, 2);
      await navigator.clipboard.writeText(jsonString);
      message.success(successMessage || 'JSON copied to clipboard');
    } catch (error) {
      message.error(errorMessage || 'Failed to copy JSON');
    }
  };

  return (
    <Button
      type={type}
      size={size}
      icon={icon}
      onClick={handleCopy}
    >
      <Trans>Copy</Trans>
    </Button>
  );
};
